import React, { useMemo } from 'react';

export default function MiniMonthPicker({
    date,
    onChange
}: { date: Date; onChange: (next: Date) => void }) {
    const year = date.getFullYear();
    const month = date.getMonth();

    const first = useMemo(() => new Date(year, month, 1), [year, month]);
    const start = useMemo(() => {
        const c = new Date(first);
        const day = c.getDay();
        c.setDate(c.getDate() - day);
        c.setHours(0, 0, 0, 0);
        return c;
    }, [first]);

    const days = useMemo(() => Array.from({ length: 42 }, (_, i) => new Date(start.getFullYear(), start.getMonth(), start.getDate() + i)), [start]);

    const isSameDay = (a: Date, b: Date) =>
        a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

    const today = new Date();

    return (
        <div className="bg-gradient-to-br from-gray-900 to-gray-800 border border-gray-700 rounded-xl p-3 shadow-lg">
            <div className="flex items-center justify-between px-1 mb-3">
                <div className="text-gray-100 text-sm font-semibold">{date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</div>
                <div className="flex gap-1">
                    <button
                        className="px-2 py-1 rounded-lg border border-gray-600 text-gray-300 hover:bg-gray-700 hover:border-gray-500 text-sm transition-all duration-200"
                        onClick={() => onChange(new Date(year, month - 1, 1))}
                    >
                        ‹
                    </button>
                    <button
                        className="px-2 py-1 rounded-lg border border-gray-600 text-gray-300 hover:bg-gray-700 hover:border-gray-500 text-sm transition-all duration-200"
                        onClick={() => onChange(new Date(year, month + 1, 1))}
                    >
                        ›
                    </button>
                </div>
            </div>
            <div className="grid grid-cols-7 text-[10px] text-gray-400 px-1 mb-2">
                {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, idx) => (
                    <div key={`${d}-${idx}`} className="text-center font-medium">{d}</div>
                ))}
            </div>
            <div className="grid grid-cols-7 gap-1">
                {days.map((d, i) => {
                    const inMonth = d.getMonth() === month;
                    const isToday = isSameDay(d, today);
                    const isSelected = isSameDay(d, date);
                    return (
                        <button
                            key={i}
                            onClick={() => onChange(d)}
                            className={`h-8 rounded-lg text-xs flex items-center justify-center border font-medium transition-all duration-200 ${inMonth ? 'text-gray-200' : 'text-gray-500'
                                } ${isSelected
                                    ? 'bg-gradient-to-br from-gray-600 to-gray-700 border-gray-500 text-white shadow-md ring-1 ring-gray-400'
                                    : 'border-gray-700 hover:bg-gray-700 hover:border-gray-600'
                                } ${isToday && !isSelected ? 'ring-1 ring-blue-500 bg-blue-900/20 border-blue-700' : ''
                                }`}
                            title={d.toDateString()}
                        >
                            {d.getDate()}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}
